import { Accessor, createSignal } from "solid-js";

import {
  type RemoteParticipant,
  LocalAudioTrack,
  LocalVideoTrack,
  Room,
  RoomEvent,
  Track,
} from "livekit-client";

import { h264Hardware, keepAllH264, prefersH264Encoding } from "./h264Hardware";
import {
  type SharedStats,
  SharedEncoder,
  sharedEncodeSupported,
} from "./sharedEncode";
import {
  DIP_HOLD_MS,
  FitLevel,
  HeldEstimate,
  MIN_FPS,
  StreamFit,
  holdEstimate,
} from "./streamFit";

/**
 * Konkord: screen share sent straight from the streamer's computer to the
 * first viewers (WebRTC peer to peer), everyone else keeps watching through
 * the voice server. Asking, offers and network addresses travel as data
 * messages inside the call, so only people in the call can connect, and the
 * video is encrypted (DTLS-SRTP) like any WebRTC call.
 *
 * Video only: the stream's sound stays on the voice server (it is small and
 * keeps the per-program mixer).
 *
 * Viewers start on the server and switch over once direct frames arrive; if
 * the direct link fails they go back to the server before the direct picture
 * is removed, so watching isn't cut either way.
 */

const TOPIC = "konkord.direct";

const ICE_SERVERS: RTCIceServer[] = [
  { urls: ["stun:stun.l.google.com:19302", "stun:stun.cloudflare.com:3478"] },
];

/**
 * Viewers served directly with a hardware (GPU / integrated) encoder.
 * Konkord: 5 (Hudson 29/10); in economy mode the streamer picks fewer (see
 * maxViewers). On our voice server streams only go directly, never through
 * the server; elsewhere the newest goes to the server when the computer or
 * the upload can't keep up
 */
const MAX_DIRECT_HARDWARE = 5;

/** Viewers served directly when the processor does the encoding */
const MAX_DIRECT_SOFTWARE = 2;

/**
 * Konkord: a camera goes to the whole call (one shared encode serves them
 * all), not limited like a stream
 */
const MAX_DIRECT_CAMERA = 12;

/**
 * Konkord: a camera is captured only as big as the biggest tile anyone
 * shows it in (from 360p up to Full HD), less work for the camera, the
 * processor and the encoder; changes wait a moment so a tile being resized
 * doesn't restart the capture over and over
 */
const CAMERA_MAX_HEIGHT = 1080;
const CAMERA_MIN_HEIGHT = 360;
const CAMERA_RESIZE_DELAY_MS = 2000;

/**
 * Konkord: without a server copy to fall back on, a failed direct link is
 * tried again after these waits (then every last one)
 */
const DIRECT_ONLY_RETRY_MS = [3000, 8000, 15_000, 30_000];

/**
 * Konkord: a viewer turned away (full) asks again this often, in case the
 * free-place offer got lost
 */
const FULL_RETRY_MS = 30_000;

/**
 * Konkord: why a stream we watch has no direct picture yet
 */
export type DirectStatus = "connecting" | "full" | "failed" | "off";

/** No direct picture this long after asking: stay on the server */
const CONNECT_TIMEOUT_MS = 8000;

/**
 * An ask sent right after joining can reach the streamer before it knows us
 * (the message arrives without a sender and is dropped): asked again this
 * often while no answer came, up to WANT_RESENDS times
 */
const WANT_RESEND_MS = 1500;
const WANT_RESENDS = 2;

/**
 * Konkord: a place offered to the first in line is kept this long for their
 * ask, then goes to the next (they stopped watching, or an older app)
 */
const SLOT_HOLD_MS = 15_000;

/** After a failed or refused attempt, don't ask that streamer again for this long */
const RETRY_MS = 60_000;

/** A connection interrupted this long is treated as lost */
const DISCONNECTED_MS = 3000;

/** Direct picture shown this long before the server copy is dropped */
const SWITCH_DELAY_MS = 1500;

/** Server copy given this long to come back before the direct picture goes */
const FALLBACK_MS = 2500;

const STATS_MS = 2000;
const FRAME_CHECK_MS = 250;
const MAX_MESSAGE_BYTES = 16_384;
/** Konkord: most programs' sounds of a stream going directly (each adds to the offer) */
const MAX_STREAM_SOUNDS = 6;
/** Konkord: same quality the stream's sound had through the server */
const STREAM_SOUND_BITRATE = 128_000;
/** Asks one viewer may make in WANT_WINDOW_MS before being turned away */
const MAX_WANTS = 6;
const WANT_WINDOW_MS = 30_000;
/** Free-place offers accepted from one streamer at most this often */
const MIN_SLOT_INTERVAL_MS = 5000;
/** Network addresses accepted from one peer */
const MAX_CANDIDATES = 50;
/** No video arriving this many checks (1 s apart) on a shown direct stream */
const STALL_CHECKS = 6;
/** Konkord: how long a new offer waits for its answer, and how many times again */
const OFFER_RETRY_MS = 4_000;
const OFFER_RETRIES = 2;
/** Video arriving but no picture coming out of it for this many checks */
const FROZEN_CHECKS = 8;
/**
 * Konkord: long before giving up on a link, the viewer tells the streamer it is
 * struggling, and a copy made for that link saves the picture: no picture for
 * this many checks, or nothing arriving at all. Only then: on a link that loses
 * packets but has room the shared encode looks far better than a copy, which
 * the browser shrinks at the first sign of trouble.
 */
const FROZEN_HINT_CHECKS = 3;
const STILL_HINT_CHECKS = 3;
/** Asked at most this often per link */
const SLOW_HINT_MS = 10_000;
/** At most this many copies made because a viewer asked (each one is an encode) */
const MAX_ASKED_COPIES = 2;
/** A copy made because a viewer asked stays at least this long */
const ASKED_COPY_MS = 60_000;
/** After falling back to the server, ask for the direct copy again after
 * these waits (one per attempt, the last one repeats) */
const RECOVER_MS = [10_000, 30_000, 60_000];
/** Calm checks before a direct place taken away under load comes back */
const CALM_CHECKS = 15;
const SIZE_DEBOUNCE_MS = 1000;
/**
 * Konkord: the stream shown nowhere (tile hidden, app minimized) this long
 * pauses our direct copy; short gaps (the layout changing) don't
 */
const PAUSE_AFTER_MS = 3000;

/** Under this bitrate a smaller picture looks better than a blurry big one */
const MIN_BITRATE = 800_000;

/**
 * Bits per pixel per frame of a direct copy. The server copy's bitrates are
 * kept low for the voice server; a direct copy only costs the streamer's
 * upload, and starved hardware encoders drop frames (a "60 FPS" stream
 * arriving at 18).
 */
const DIRECT_BITS_PER_PIXEL = 0.06;
const MAX_DIRECT_BITRATE = 12_000_000;

/**
 * Konkord: a viewer whose link carries less than this share of the shared
 * encode's bitrate, SLOW_CHECKS times in a row (2 s each), gets its own copy
 * adapted to its link instead of pulling everyone's picture down (31/10: a
 * phone on 4G froze the stream for a viewer on a good network too); back on
 * the shared one after FAST_CHECKS in a row carrying FAST_LINK of it. Not in
 * the first LINK_GRACE_MS of a connection: the browser's estimate of a link
 * starts low and climbs for several seconds
 */
const SLOW_LINK = 0.6;
const SLOW_CHECKS = 5;
const LINK_GRACE_MS = 20_000;
const FAST_LINK = 0.9;
const FAST_CHECKS = 15;
/** Konkord: a viewer saying its picture failed waits this long to hear from the others */
const UPLOAD_WAIT_MS = 4000;
/** Everyone having said so this recently means it's the streamer's upload */
const UPLOAD_HEARD_MS = 6000;
/** Konkord: losing more than this share of packets is a link at its limit */
const LOSS_FRACTION = 0.02;
/** With no such loss for this long, the shared encode may try above the estimate */
const LOSS_CALM_MS = 6000;
/** A link that lost packets this recently isn't climbing back from a spike */
const FIT_LOSSY_MS = 4000;
/** Each try that loses packets again waits twice as long, up to this */
const LOSS_CALM_MAX_MS = 60_000;
/** This long with nothing lost and a link has changed: back to the short wait */
const LOSS_FORGOTTEN_MS = 2 * 60_000;
/** For this long after that, one more word about a failing picture changes nothing */
const UPLOAD_TROUBLE_MS = 20_000;
/**
 * Konkord: an own copy can't prove its link got better: little picture, so the
 * browser estimates little, so little picture (31 s of good internet left a viewer
 * at 180p for good). So it tries the shared encode again now and then, not counted
 * as slow while its estimate climbs. Back within OWN_PROVED_MS, it waits twice as
 * long before the next try.
 */
const OWN_TRIAL_MS = 60_000;
const OWN_TRIAL_MAX_MS = 10 * 60_000;
const OWN_TRIAL_GRACE_MS = 20_000;
const OWN_PROVED_MS = 60_000;
/** Konkord: a slow link's own copy is at most this tall (it can't carry more) */
const SLOW_LINK_HEIGHT = 720;

/** Heights a viewer's tile is rounded up to */
const HEIGHT_STEPS = [360, 540, 720, 1080, 1440];

type Message =
  // Konkord: h264 false when the viewer can't decode H.264 (no shared encode)
  | { t: "want"; sid: string; h264?: boolean }
  | { t: "no"; sid: string; why: "off" | "full" | "busy" }
  // Konkord: an offer says what the streamer runs, for the count of how
  // links go (see DirectResult.streamer)
  | { t: "offer" | "answer"; sid: string; sdp: string; from?: string }
  | { t: "ice"; sid: string; c: RTCIceCandidateInit }
  | { t: "size"; sid: string; h: number }
  // Konkord: which sound is which: the connection's mid and the track's name
  // (a program's, see appAudio.ts)
  | { t: "sounds"; sid: string; list: { mid: string; name: string }[] }
  | { t: "bye"; sid: string }
  | { t: "slow"; sid: string }
  | { t: "slot" }
  // Konkord: streamer → viewer in line: your place (1: next); viewer →
  // streamer: no longer waiting
  | { t: "queue"; pos: number }
  | { t: "leave" };

/**
 * Konkord: what our direct sending looks like, for the stats panel
 */
export type DirectSending = {
  /** Viewers connected directly */
  viewers: number;
  /** Most it accepts right now */
  limit: number;
  /** Konkord: people in line for a place */
  waiting: number;
  /** Encoder in use, e.g. "MediaFoundationVideoEncodeAccelerator" */
  encoder?: string;
  /** Hardware encoder (GPU or integrated graphics), undefined while unknown */
  hardware?: boolean;
  /** Why the browser lowered quality on a direct copy: cpu, bandwidth, other or none */
  reason: string;
  /** Each direct copy going out */
  copies: DirectCopy[];
};

/**
 * Konkord: one direct copy going out, for the developer panel
 */
export type DirectCopy = {
  /** Konkord: what the link carries by the browser's estimate; on an own copy */
  available?: number;
  own?: boolean;
  identity: string;
  width: number;
  height: number;
  fps: number;
  /** Bits per second actually sent */
  bitrate: number;
  /** Average time the encoder took per frame */
  encodeMs?: number;
  limit: string;
};

export type SendSample = {
  at: number;
  bytes: number;
  encodeTime: number;
  frames: number;
};

/**
 * Konkord: rate and encoding time between two samples of an outgoing stream
 */
export function sendRates(previous: SendSample | undefined, now: SendSample) {
  const seconds = previous ? (now.at - previous.at) / 1000 : 0;
  const frames = previous ? now.frames - previous.frames : 0;
  return {
    // counters restart when the stream is published again
    bitrate:
      previous && seconds > 0
        ? Math.max(0, ((now.bytes - previous.bytes) * 8) / seconds)
        : 0,
    encodeMs:
      previous && frames > 0
        ? Math.max(0, ((now.encodeTime - previous.encodeTime) * 1000) / frames)
        : undefined,
  };
}

/**
 * Konkord: a sound of a stream we watch, arriving directly
 */
export type DirectSound = {
  identity: string;
  name: string;
  track: MediaStreamTrack;
};

type SoundSlot = {
  transceiver: RTCRtpTransceiver;
  name?: string;
  tuned?: boolean;
};

type Outgoing = {
  sid: string;
  identity: string;
  pc: RTCPeerConnection;
  sender: RTCRtpSender;
  /** Konkord: the stream's sounds on this connection */
  sounds: SoundSlot[];
  soundsTold?: string;
  /** one change at a time */
  soundWork: Promise<void>;
  answerTimer?: ReturnType<typeof setTimeout>;
  /** The viewer asked for a copy of its own: not before this goes back */
  ownUntil?: number;
  /** Konkord: a new sound while an offer was being answered */
  reoffer?: boolean;
  connected: boolean;
  timer: ReturnType<typeof setTimeout>;
  /** Tile height reported by the viewer, in device pixels */
  height?: number;
  /** Encoding last applied, to re-apply when the stream format changes */
  applied?: string;
  candidates: number;
  /** Last stats sample, for rates */
  sample?: SendSample;
  /** Konkord: the viewer shows it nowhere right now, nothing is encoded */
  paused?: boolean;
  /** Konkord: fed by the one shared encode (see sharedEncode.ts) */
  shared?: ReturnType<SharedEncoder["attach"]>;
  /** Konkord: what this viewer's link carries (bits/s), from its stats */
  available?: number;
  /** Konkord: its estimate through a sudden drop, see holdEstimate */
  heldEstimate?: HeldEstimate;
  /**
   * Konkord: on the shared encode's connection but encoding its own copy
   * (a link too slow for the shared one), and the checks counting towards
   * a switch (see SLOW_LINK)
   */
  own?: boolean;
  slowChecks?: number;
  /** Konkord: the size of its own copy, see #ownHeight */
  ownHeight?: number;
  /** Konkord: when the viewer last said its picture failed */
  slowAt?: number;
  /** Konkord: an own copy's next try on the shared encode, see OWN_TRIAL_MS */
  ownTrialAt?: number;
  ownTrialWait?: number;
  /** Konkord: the last try started then, its estimate still climbing until */
  trialAt?: number;
  trialUntil?: number;
  fastChecks?: number;
  /** Konkord: when it last lost packets (see #graceOver) */
  lostAt?: number;
  /** Konkord: when it connected (see LINK_GRACE_MS) */
  connectedAt?: number;
};

type Incoming = {
  sid: string;
  identity: string;
  pc: RTCPeerConnection;
  stream?: MediaStream;
  /** Konkord: the sounds' tracks by mid, and their names from the streamer */
  soundTracks: Map<string, MediaStreamTrack>;
  soundNames: { mid: string; name: string }[];
  receiver?: RTCRtpReceiver;
  remoteSet: boolean;
  queued: RTCIceCandidateInit[];
  timers: ReturnType<typeof setTimeout>[];
  frameCheck?: ReturnType<typeof setInterval>;
  sizeTimer?: ReturnType<typeof setTimeout>;
  candidates: number;
  /** Konkord: turned away or ended by the streamer (not a failed link) */
  refused?: boolean;
  /** Konkord: its result (worked or failed) was reported */
  reported?: boolean;
  /** Konkord: we asked the streamer to pause (shown nowhere) */
  paused?: boolean;
  /** Konkord: for the anonymous count of how links go (see DirectResult) */
  started: number;
  streamer?: string;
  attempt: number;
  connected?: boolean;
  pair?: string;
  local: Set<string>;
  remote: Set<string>;
  /** public addresses, only compared here (never sent anywhere) */
  publicLocal: Set<string>;
  publicRemote: Set<string>;
};

/**
 * Konkord: how a direct link went, counted anonymously to find out why links
 * fail (no addresses, nothing about who)
 */
export type DirectResult = {
  ok: boolean;
  /**
   * where a failed link stopped: no offer came (signal), the networks never
   * reached each other (ice), connected but no picture (media), connected
   * then cut (drop), the picture stopped (stall) or froze (frozen), or an
   * error setting it up
   */
  stage?: "signal" | "ice" | "media" | "drop" | "stall" | "frozen" | "error";
  /** asks for this stream in a row before this one (0: the first) */
  attempt: number;
  /** kinds of our / their network candidates, e.g. "host6,srflx4" */
  local: string;
  remote: string;
  /** the candidates that connected, ours>theirs, e.g. "srflx4>host6" */
  pair?: string;
  /** it had worked (pictures came), then broke */
  broke?: boolean;
  /** the streamer's app and encode, e.g. "edge.shared.hw" (from its offer) */
  streamer?: string;
  /** our app: desktop, chrome, edge, firefox, safari or other */
  app: string;
  /** both behind the same public address (the same home network) */
  sameNetwork: boolean;
  ms: number;
};

/**
 * Konkord: which app this is, for the anonymous count of how links go
 */
function appName() {
  if (typeof window !== "undefined" && window.native) return "desktop";
  const agent = typeof navigator === "undefined" ? "" : navigator.userAgent;
  return /Edg\//.test(agent)
    ? "edge"
    : /Firefox\//.test(agent)
      ? "firefox"
      : /Chrome\//.test(agent)
        ? "chrome"
        : /Safari\//.test(agent)
          ? "safari"
          : "other";
}

/**
 * Konkord: short kind of a candidate: its type, address family (4, 6 or mdns
 * for a hidden local address), tcp, and lan for a home network address
 */
function candidateKind(candidate: string) {
  const parts = candidate.split(" ");
  const typ = parts.indexOf("typ");
  const type = typ > 0 ? parts[typ + 1] : "unknown";
  const address = candidateAddress(candidate);
  const family = address.endsWith(".local")
    ? "mdns"
    : address.includes(":")
      ? "6"
      : "4";
  return (
    type +
    family +
    (parts[2]?.toLowerCase() === "tcp" ? "tcp" : "") +
    (PRIVATE_ADDRESS.test(address) ? "lan" : "")
  ).replace(/[^a-z0-9]/g, "");
}

/**
 * Konkord: the public address a candidate shows (its own when public, the
 * found one for srflx / prflx)
 */
function publicAddress(candidate: string) {
  const address = candidateAddress(candidate);
  return address &&
    !address.endsWith(".local") &&
    !PRIVATE_ADDRESS.test(address)
    ? address
    : undefined;
}

/**
 * Konkord: network addresses inside the home / company network (and their
 * IPv6 equivalents) are never sent nor tried: they say nothing useful to
 * someone elsewhere and would let a call member probe our local network
 */
const PRIVATE_ADDRESS =
  /^(10\.|127\.|169\.254\.|192\.168\.|172\.(1[6-9]|2\d|3[01])\.|0\.|fe80:|f[cd][0-9a-f]{2}:|::1$)/i;

function candidateAddress(candidate: string) {
  return candidate.split(" ")[4] ?? "";
}

/**
 * Konkord: our own candidate as sent to the other side, or undefined when it
 * only carries a local address; the local address a public one was found
 * from (raddr) is blanked
 */
function shareableCandidate(candidate: RTCIceCandidate) {
  const init = candidate.toJSON();
  if (!init.candidate || PRIVATE_ADDRESS.test(candidateAddress(init.candidate)))
    return;
  init.candidate = init.candidate.replace(
    / raddr \S+ rport \d+/,
    " raddr 0.0.0.0 rport 0",
  );
  return init;
}

/**
 * Konkord: a description with no addresses in it. They only travel one by one,
 * checked above: an offer made after they were gathered lists them all, the
 * local ones too, and takes space in a message of limited size. Also on what
 * arrives, so nobody in a call can make us try addresses of our own network.
 */
export function withoutCandidates(sdp: string) {
  return sdp.replace(/^a=(?:candidate:|end-of-candidates).*\r?\n/gm, "");
}

/**
 * Konkord: a candidate from the other side we are willing to try
 */
function acceptableCandidate(candidate: unknown) {
  const init = candidate as RTCIceCandidateInit | undefined;
  if (!init || typeof init.candidate !== "string") return;
  if (init.candidate.length > 512) return;
  if (init.candidate && PRIVATE_ADDRESS.test(candidateAddress(init.candidate)))
    return;
  return {
    candidate: init.candidate,
    sdpMid: typeof init.sdpMid === "string" ? init.sdpMid : undefined,
    sdpMLineIndex:
      typeof init.sdpMLineIndex === "number" ? init.sdpMLineIndex : undefined,
  } satisfies RTCIceCandidateInit;
}

/**
 * Konkord: whether we decode H.264 (Firefox may lack it without OpenH264):
 * told to streamers, whose shared encode is H.264 only
 */
const CAN_RECEIVE_H264 =
  typeof RTCRtpReceiver === "undefined" ||
  !RTCRtpReceiver.getCapabilities ||
  !!RTCRtpReceiver.getCapabilities("video")?.codecs.some(
    (codec) => codec.mimeType.toLowerCase() === "video/h264",
  );

const encoder = new TextEncoder();
const decoder = new TextDecoder();

const newSid = () =>
  Math.random().toString(36).slice(2, 10) + Date.now().toString(36);

export class DirectStreams {
  #room: Room;
  /** Konkord: what goes directly: the screen share, or the camera */
  #kind: Track.Source.ScreenShare | Track.Source.Camera;
  /** Konkord: data channel topic, one per kind (they don't mix) */
  #topic: string;
  #enabled: () => boolean;
  #directSound: () => boolean;
  #sounds: Accessor<ReadonlyMap<string, DirectSound[]>>;
  #setSounds: (sounds: ReadonlyMap<string, DirectSound[]>) => void;
  #onChange: () => void;
  #onSlotHandler?: (identity: string) => void;
  /** Konkord: most viewers this stream may have (6, or 3 in economy mode) */
  #maxViewers: () => number;
  /** Konkord: whether the server copy can stand in for a direct one */
  #serverFallback: () => boolean;
  /** Konkord: each direct link asked for worked (pictures came) or failed */
  #onResult?: (result: DirectResult) => void;
  #fit = new StreamFit();
  #uploadTroubleUntil = 0;
  #lossAt = -Infinity;
  #lossWait = LOSS_CALM_MS;
  #fitOn: () => boolean;
  #onFit?: (level: FitLevel | undefined) => void;
  /** Konkord: asks in a row that didn't work, per streamer (for the count) */
  #attempts = new Map<string, number>();
  /** Streams we want directly (until unwatch), to keep asking */
  #wanted = new Set<string>();
  #retryTimers = new Map<string, ReturnType<typeof setTimeout>>();
  #status;
  #setStatus;

  #out = new Map<string, Outgoing>();
  #in = new Map<string, Incoming>();
  /** Viewers turned away while full, offered the next free place */
  /** Konkord: the line for a place, in order (first: next to get in) */
  #waiting = new Set<string>();
  /** Konkord: when the first in line were offered a place (see SLOT_HOLD_MS) */
  #offeredAt = new Map<string, number>();
  /** Konkord: the place in line each one was last told */
  #toldPlace = new Map<string, number>();
  /** Konkord: our place in each streamer's line, while we wait */
  #place;
  #setPlace;
  /** Recent asks of each viewer */
  #wants = new Map<string, number[]>();
  /** Last free-place offer from each streamer */
  #lastSlot = new Map<string, number>();
  /** When we may ask each streamer again after a failure */
  #retryAt = new Map<string, number>();
  /** Falls back to the server in a row, per streamer (longer waits each time) */
  #fallbacks = new Map<string, number>();
  #recoverTimers = new Map<string, ReturnType<typeof setTimeout>>();
  /** Direct pictures kept a moment after falling back, closed early on close() */
  #pendingRemovals = new Set<() => void>();
  /** Shown sizes of each direct stream, per video element */
  #sizes = new Map<string, Map<object, number>>();
  /** Direct places for this stream, lowered when the computer struggles */
  #cap = MAX_DIRECT_HARDWARE; // set again in the constructor (per kind)
  #pressure = 0;
  #calm = 0;
  /** Konkord: one encode for every direct viewer, while it works */
  #shared?: SharedEncoder;
  #sharedBroken = false;
  /**
   * Konkord: camera capture height asked for, the pending change and the
   * latest height wanted while it waits
   */
  #captureHeight = CAMERA_MAX_HEIGHT;
  #captureTimer?: ReturnType<typeof setTimeout>;
  #captureWanted = CAMERA_MAX_HEIGHT;
  #sharedStats?: SharedStats;
  #checking = false;
  #statsTimer: ReturnType<typeof setInterval>;
  /** Shutting down: goodbyes are still sent, nothing new starts */
  #closing = false;
  #closed = false;

  #streams;
  #setStreams;
  /** Identities whose server copy is no longer needed */
  #covered;
  #setCovered;
  sending;
  #setSending;

  constructor(
    room: Room,
    options: {
      enabled: () => boolean;
      /** Which streams need their server copy changed */
      onChange: () => void;
      /** A streamer has a direct place for us again */
      onSlot: (identity: string) => void;
      /** Most viewers of our stream (defaults to the encoder's limit) */
      maxViewers?: () => number;
      /** Whether the server copy may stand in (defaults to yes) */
      serverFallback?: () => boolean;
      /**
       * A direct link worked (pictures came) or failed, once per ask; and
       * when one that worked breaks later (broke)
       */
      onResult?: (result: DirectResult) => void;
      /** Konkord: the camera instead of the screen share */
      kind?: Track.Source.ScreenShare | Track.Source.Camera;
      /** Konkord: follow the streamer's upload, see StreamFit (defaults to yes) */
      fit?: () => boolean;
      /** Konkord: the stream was lowered for the upload, or is back (undefined) */
      onFit?: (level: FitLevel | undefined) => void;
      /**
       * Konkord: the stream's sound goes directly too, nothing of it through
       * the server (its tracks stay published, for the mixer and the icons)
       */
      directSound?: () => boolean;
    },
  ) {
    this.#room = room;
    this.#kind = options.kind ?? Track.Source.ScreenShare;
    this.#topic =
      this.#kind === Track.Source.Camera ? `${TOPIC}.camera` : TOPIC;
    this.#enabled = options.enabled;
    this.#onChange = options.onChange;
    this.#onSlotHandler = options.onSlot;
    this.#cap = this.#hardwareMax();
    this.#maxViewers = options.maxViewers ?? (() => this.#hardwareMax());
    this.#serverFallback = options.serverFallback ?? (() => true);
    this.#onResult = options.onResult;
    this.#fitOn = options.fit ?? (() => true);
    this.#onFit = options.onFit;
    this.#directSound = options.directSound ?? (() => false);
    [this.#sounds, this.#setSounds] = createSignal<
      ReadonlyMap<string, DirectSound[]>
    >(new Map());
    [this.#status, this.#setStatus] = createSignal<
      ReadonlyMap<string, DirectStatus>
    >(new Map());

    [this.#streams, this.#setStreams] = createSignal<
      ReadonlyMap<string, MediaStream>
    >(new Map());
    [this.#place, this.#setPlace] = createSignal<ReadonlyMap<string, number>>(
      new Map(),
    );
    [this.#covered, this.#setCovered] = createSignal<ReadonlySet<string>>(
      new Set(),
    );
    [this.sending, this.#setSending] = createSignal<DirectSending>();

    room.on(RoomEvent.DataReceived, this.#onData);
    room.on(RoomEvent.LocalTrackUnpublished, this.#onLocalUnpublished);
    room.on(RoomEvent.LocalTrackPublished, this.#onLocalPublished);
    room.on(RoomEvent.ParticipantDisconnected, this.#onParticipantLeft);
    // Konkord: the app hidden pauses the copies sent to us (see #sendSize)
    document.addEventListener("visibilitychange", this.#onVisibility);
    this.#statsTimer = setInterval(() => void this.#checkSending(), STATS_MS);
  }

  /**
   * Direct picture of a stream we watch, once frames arrive
   */
  stream(identity: string) {
    return this.#streams().get(identity);
  }

  /**
   * Konkord: the sounds arriving directly from the streams we watch
   */
  sounds() {
    return [...this.#sounds().values()].flat();
  }

  /**
   * Receiver of a direct stream, for the stats panel
   */
  receiver(identity: string) {
    return this.#in.get(identity)?.receiver;
  }

  /**
   * Konkord: why a watched stream has no direct picture (undefined once it
   * shows, or when not watching)
   */
  status(identity: string) {
    return this.#status().get(identity);
  }

  /**
   * Konkord: our place in the streamer's line while their stream is full
   * (1: next to get in), undefined when not waiting
   */
  placeInLine(identity: string) {
    return this.#place().get(identity);
  }

  #setOurPlace(identity: string, place: number | undefined) {
    if (this.#place().get(identity) === place) return;
    const next = new Map(this.#place());
    if (place) next.set(identity, place);
    else next.delete(identity);
    this.#setPlace(next);
  }

  /**
   * Konkord: the streamer changed how many watch directly: applied now (the
   * last ones to start watching beyond it wait first in line, a higher
   * limit lets the line in)
   */
  applyLimit() {
    if (this.#closing) return;
    this.#trimToLimit();
    this.#offerSlots();
    this.#tellPlaces();
    void this.#checkSending();
  }

  #setWhy(identity: string, why: DirectStatus | undefined) {
    if (this.#status().get(identity) === why) return;
    const next = new Map(this.#status());
    if (why) next.set(identity, why);
    else next.delete(identity);
    this.#setStatus(next);
  }

  /** Konkord: most direct places for this kind (camera: the whole call) */
  #hardwareMax() {
    return this.#kind === Track.Source.Camera
      ? MAX_DIRECT_CAMERA
      : MAX_DIRECT_HARDWARE;
  }

  /** Konkord: direct places of our stream right now */
  #limit() {
    return Math.max(1, Math.min(this.#cap, this.#maxViewers()));
  }

  /**
   * Konkord: with no server copy, ask again by ourselves after a failure or
   * a "full" (in case the free-place offer is lost)
   */
  #scheduleRetry(identity: string, why: DirectStatus) {
    if (this.#serverFallback() || this.#closing) return;
    if (!this.#wanted.has(identity)) return;
    clearTimeout(this.#retryTimers.get(identity));
    const failures = this.#fallbacks.get(identity) ?? 0;
    const wait =
      why === "full"
        ? FULL_RETRY_MS
        : DIRECT_ONLY_RETRY_MS[
            Math.min(failures, DIRECT_ONLY_RETRY_MS.length - 1)
          ];
    if (why !== "full") this.#fallbacks.set(identity, failures + 1);
    this.#retryTimers.set(
      identity,
      setTimeout(() => {
        this.#retryTimers.delete(identity);
        if (!this.#wanted.has(identity) || this.#in.has(identity)) return;
        this.#retryAt.delete(identity);
        this.watch(identity);
      }, wait),
    );
  }

  /**
   * Whether the server copy of this stream can be left out
   */
  covers(identity: string) {
    return this.#covered().has(identity);
  }

  /**
   * Ask a streamer for a direct copy (nothing happens if they can't)
   */
  watch(identity: string) {
    if (this.#closing) return;
    this.#wanted.add(identity);
    if (!this.#enabled()) return this.#setWhy(identity, "off");
    if (this.#in.has(identity)) return;
    if ((this.#retryAt.get(identity) ?? 0) > Date.now()) return;
    if (!this.#streams().has(identity)) this.#setWhy(identity, "connecting");

    const sid = newSid();
    const pc = new RTCPeerConnection({ iceServers: ICE_SERVERS });
    const entry: Incoming = {
      sid,
      identity,
      pc,
      soundTracks: new Map(),
      soundNames: [],
      remoteSet: false,
      queued: [],
      timers: [],
      candidates: 0,
      started: Date.now(),
      attempt: this.#attempts.get(identity) ?? 0,
      local: new Set(),
      remote: new Set(),
      publicLocal: new Set(),
      publicRemote: new Set(),
    };
    this.#in.set(identity, entry);

    pc.onicecandidate = (event) => {
      if (event.candidate?.candidate) {
        entry.local.add(candidateKind(event.candidate.candidate));
        const address = publicAddress(event.candidate.candidate);
        if (address) entry.publicLocal.add(address);
      }
      const c = event.candidate && shareableCandidate(event.candidate);
      if (c) this.#send(identity, { t: "ice", sid, c });
    };
    pc.ontrack = (event) => {
      if (event.track.kind === "audio") {
        if (event.transceiver.mid) {
          entry.soundTracks.set(event.transceiver.mid, event.track);
          this.#showSounds(entry);
        }
        return;
      }
      if (event.track.kind !== "video") return;
      entry.receiver = event.receiver;
      entry.stream = new MediaStream([event.track]);
      this.#waitForFrames(entry);
    };
    pc.onconnectionstatechange = () => {
      if (pc.connectionState === "connected" && !entry.connected) {
        entry.connected = true;
        void this.#notePair(entry);
      }
      if (pc.connectionState === "failed" || pc.connectionState === "closed")
        this.#dropIncoming(identity, sid, true);
      else if (pc.connectionState === "disconnected")
        entry.timers.push(
          setTimeout(() => {
            if (pc.connectionState === "disconnected")
              this.#dropIncoming(identity, sid, true);
          }, DISCONNECTED_MS),
        );
    };

    entry.timers.push(
      setTimeout(() => {
        if (!this.#streams().has(identity))
          this.#dropIncoming(identity, sid, true);
      }, CONNECT_TIMEOUT_MS),
    );

    this.#send(identity, { t: "want", sid, h264: CAN_RECEIVE_H264 });
    let resends = 0;
    const resend = () => {
      // answered (an offer, or a "no" that ended this ask)
      if (this.#in.get(identity) !== entry || entry.remoteSet) return;
      this.#send(identity, { t: "want", sid, h264: CAN_RECEIVE_H264 });
      if (++resends < WANT_RESENDS)
        entry.timers.push(setTimeout(resend, WANT_RESEND_MS));
    };
    entry.timers.push(setTimeout(resend, WANT_RESEND_MS));
  }

  /**
   * Stop receiving a stream directly
   */
  unwatch(identity: string) {
    this.#wanted.delete(identity);
    clearTimeout(this.#retryTimers.get(identity));
    this.#retryTimers.delete(identity);
    this.#setWhy(identity, undefined);
    this.#retryAt.delete(identity);
    this.#attempts.delete(identity);
    this.#sizes.delete(identity);
    this.#fallbacks.delete(identity);
    clearTimeout(this.#recoverTimers.get(identity));
    this.#recoverTimers.delete(identity);
    // Konkord: our place in line goes to the next
    if (this.#place().has(identity)) {
      this.#setOurPlace(identity, undefined);
      this.#send(identity, { t: "leave" });
    }
    const entry = this.#in.get(identity);
    if (!entry) return;
    this.#send(identity, { t: "bye", sid: entry.sid });
    this.#dropIncoming(identity, entry.sid, false);
  }

  /**
   * How big a video element shows a direct stream (device pixels, 0 once
   * gone), so the streamer sends no more than the biggest one needs
   */
  reportSize(identity: string, element: object, height: number) {
    const sizes = this.#sizes.get(identity) ?? new Map<object, number>();
    if (height > 0) sizes.set(element, height);
    else sizes.delete(element);
    this.#sizes.set(identity, sizes);
    this.#sendSize(identity);
  }

  /**
   * Konkord: tell the streamer the biggest size we show their stream at; 0
   * (shown nowhere, or the app hidden) pauses their copy for us after a
   * moment, and showing it again resumes it right away
   */
  #sendSize(identity: string) {
    const entry = this.#in.get(identity);
    const sizes = this.#sizes.get(identity);
    // nothing to pause before the first frame (the tile appears with it)
    if (!entry || !sizes || !this.#streams().has(identity)) return;
    const biggest = document.hidden ? 0 : Math.max(0, ...sizes.values());
    clearTimeout(entry.sizeTimer);
    const send = () => {
      entry.paused = biggest === 0;
      this.#send(identity, {
        t: "size",
        sid: entry.sid,
        h: Math.round(biggest),
      });
    };
    if (biggest && entry.paused) send();
    else
      entry.sizeTimer = setTimeout(
        send,
        biggest ? SIZE_DEBOUNCE_MS : PAUSE_AFTER_MS,
      );
  }

  #onVisibility = () => {
    for (const identity of this.#in.keys()) this.#sendSize(identity);
  };

  /**
   * Our screen share got new pictures (another screen or window)
   */
  refreshSource() {
    const track = this.#source();
    // Konkord: a new camera capture starts at its full size
    this.#captureHeight = CAMERA_MAX_HEIGHT;
    if (track) this.#shared?.setSource(track);
    for (const out of [...this.#out.values()]) {
      if (!track) this.#endOutgoing(out.identity, true);
      // Konkord: shared ones send the stand-in, fed from the new source above
      // (those on their own copy take the new source too)
      else if ((!out.shared || out.own) && out.sender.track !== track)
        void out.sender.replaceTrack(track).catch(() => {});
    }
  }

  /**
   * Konkord: the stream's sounds changed (a program started or stopped, or
   * the PC's sound was swapped): every direct viewer follows
   */
  refreshSound() {
    this.#soundSources();
    for (const out of this.#out.values()) this.#queueSounds(out);
  }

  close() {
    if (this.#closing) return;
    this.#closing = true;
    this.#waiting.clear();
    for (const identity of [...this.#out.keys()])
      this.#endOutgoing(identity, true);
    for (const [identity, entry] of [...this.#in]) {
      this.#send(identity, { t: "bye", sid: entry.sid });
      this.#dropIncoming(identity, entry.sid, false);
    }
    for (const run of [...this.#pendingRemovals]) run();
    this.#closed = true;
    clearInterval(this.#statsTimer);
    clearTimeout(this.#captureTimer);
    this.#recoverTimers.forEach(clearTimeout);
    this.#recoverTimers.clear();
    this.#retryTimers.forEach(clearTimeout);
    this.#retryTimers.clear();
    this.#wanted.clear();
    this.#setStatus(new Map());
    this.#room.off(RoomEvent.DataReceived, this.#onData);
    this.#room.off(RoomEvent.LocalTrackUnpublished, this.#onLocalUnpublished);
    this.#room.off(RoomEvent.LocalTrackPublished, this.#onLocalPublished);
    this.#room.off(RoomEvent.ParticipantDisconnected, this.#onParticipantLeft);
    document.removeEventListener("visibilitychange", this.#onVisibility);
    this.#setStreams(new Map());
    this.#setSounds(new Map());
    this.#setCovered(new Set<string>());
    this.#setSending();
  }

  // --- signalling ---

  #send(identity: string, message: Message) {
    if (this.#closed) return;
    void this.#room.localParticipant
      .publishData(encoder.encode(JSON.stringify(message)), {
        reliable: true,
        topic: this.#topic,
        destinationIdentities: [identity],
      })
      .catch(() => {});
  }

  #onData = (
    payload: Uint8Array,
    participant?: RemoteParticipant,
    _kind?: unknown,
    topic?: string,
  ) => {
    if (topic !== this.#topic || !participant || this.#closing) return;
    if (payload.byteLength > MAX_MESSAGE_BYTES) return;

    let message: Message;
    try {
      message = JSON.parse(decoder.decode(payload));
    } catch {
      return;
    }
    if (!message || typeof message !== "object") return;
    if (
      message.t !== "slot" &&
      message.t !== "queue" &&
      message.t !== "leave" &&
      (typeof message.sid !== "string" || message.sid.length > 40)
    )
      return;

    const from = participant.identity;
    switch (message.t) {
      case "want":
        return void this.#onWant(from, message.sid, message.h264 !== false);
      case "answer":
        return void this.#onAnswer(from, message);
      case "size":
        return this.#onSize(from, message);
      case "slow":
        return void this.#onSlow(from, message.sid);
      case "sounds":
        return this.#onSounds(from, message);
      case "offer":
        this.#setOurPlace(from, undefined);
        return void this.#onOffer(from, message);
      case "queue": {
        // only while we want that stream and it's full for us
        if (!this.#wanted.has(from) || this.#in.has(from)) return;
        const pos = Number(message.pos);
        if (Number.isInteger(pos) && pos >= 1 && pos <= 999)
          this.#setOurPlace(from, pos);
        return;
      }
      case "leave":
        if (!this.#waiting.delete(from)) return;
        this.#offeredAt.delete(from);
        this.#toldPlace.delete(from);
        this.#offerSlots();
        this.#tellPlaces();
        return;
      case "no": {
        const asked = this.#in.get(from);
        if (asked?.sid !== message.sid) return;
        asked.refused = true;
        this.#dropIncoming(from, message.sid, true);
        // Konkord: the streamer's answer says more than "failed"
        const why = message.why === "off" ? "off" : "full";
        this.#setWhy(from, why);
        this.#scheduleRetry(from, why);
        return;
      }
      case "slot": {
        // only matters while still watching on the server
        const now = Date.now();
        if (this.#in.has(from)) return;
        if (now - (this.#lastSlot.get(from) ?? 0) < MIN_SLOT_INTERVAL_MS)
          return;
        this.#lastSlot.set(from, now);
        this.#retryAt.delete(from);
        this.#onSlotHandler?.(from);
        return;
      }
      case "ice":
        return void this.#onIce(from, message);
      case "bye": {
        const asked = this.#in.get(from);
        if (asked?.sid === message.sid) asked.refused = true;
        this.#dropIncoming(from, message.sid, true);
        if (this.#out.get(from)?.sid === message.sid)
          this.#endOutgoing(from, false);
        return;
      }
    }
  };

  #onLocalPublished = (publication: { source: Track.Source }) => {
    if (publication.source === Track.Source.ScreenShareAudio)
      this.refreshSound();
  };

  #onLocalUnpublished = (publication: { source: Track.Source }) => {
    if (publication.source === Track.Source.ScreenShareAudio)
      this.refreshSound();
    if (publication.source === this.#kind) {
      for (const identity of [...this.#out.keys()])
        this.#endOutgoing(identity, true);
      this.#waiting.clear();
      this.#offeredAt.clear();
      this.#toldPlace.clear();
      this.#cap = this.#hardwareMax();
      this.#pressure = 0;
    }
  };

  #onParticipantLeft = (participant: RemoteParticipant) => {
    const identity = participant.identity;
    // Konkord: their place in line goes to the next
    if (this.#waiting.delete(identity)) {
      this.#offeredAt.delete(identity);
      this.#toldPlace.delete(identity);
      this.#offerSlots();
      this.#tellPlaces();
    }
    this.#setOurPlace(identity, undefined);
    this.#wants.delete(identity);
    this.#lastSlot.delete(identity);
    this.#retryAt.delete(identity);
    this.#sizes.delete(identity);
    this.#fallbacks.delete(identity);
    this.#attempts.delete(identity);
    clearTimeout(this.#recoverTimers.get(identity));
    this.#recoverTimers.delete(identity);
    clearTimeout(this.#retryTimers.get(identity));
    this.#retryTimers.delete(identity);
    this.#setWhy(identity, undefined);
    // also told: a full reconnect looks like leaving for a moment
    if (this.#out.has(identity)) this.#endOutgoing(identity, true);
    const entry = this.#in.get(identity);
    if (entry) this.#dropIncoming(identity, entry.sid, false);
  };

  // --- streamer side ---

  #source() {
    const track = this.#room.localParticipant.getTrackPublication(
      this.#kind,
    )?.videoTrack;
    if (!(track instanceof LocalVideoTrack) || track.isUpstreamPaused) return;
    const media = track.mediaStreamTrack;
    return media.readyState === "live" ? media : undefined;
  }

  /**
   * Bitrate and frame rate of the copy sent to the server (its best layer)
   */
  #format() {
    const picked = this.#picked();
    const fit = this.#fitted();
    if (!picked || !fit || fit.fps >= picked.fps) return picked;
    return { bitrate: (picked.bitrate * fit.fps) / picked.fps, fps: fit.fps };
  }

  /** Konkord: the level the upload allows, when it's below what was picked */
  #fitted() {
    return this.#kind === Track.Source.ScreenShare &&
      this.#fitOn() &&
      this.#fit.lowered
      ? this.#fit.level
      : undefined;
  }

  /** What the streamer picked, before the upload has its say */
  #picked() {
    const track = this.#room.localParticipant.getTrackPublication(
      this.#kind,
    )?.videoTrack;
    const encodings =
      track instanceof LocalVideoTrack
        ? (track.sender?.getParameters().encodings ?? [])
        : [];
    const best = encodings.reduce<RTCRtpEncodingParameters | undefined>(
      (top, encoding) =>
        (encoding.maxBitrate ?? 0) > (top?.maxBitrate ?? 0) ? encoding : top,
      undefined,
    );
    if (!best?.maxBitrate) return;
    return { bitrate: best.maxBitrate, fps: best.maxFramerate ?? 30 };
  }

  async #onWant(identity: string, sid: string, h264 = true) {
    if (this.#out.get(identity)?.sid === sid) return;

    // a few asks per viewer, more is someone hammering us
    const now = Date.now();
    const recent = (this.#wants.get(identity) ?? []).filter(
      (at) => now - at < WANT_WINDOW_MS,
    );
    recent.push(now);
    this.#wants.set(identity, recent);
    if (recent.length > MAX_WANTS)
      return this.#send(identity, { t: "no", sid, why: "busy" });

    if (!this.#enabled())
      return this.#send(identity, { t: "no", sid, why: "off" });

    // paused (e.g. while the quality is being picked): offered once it runs
    const track = this.#source();
    if (!track) {
      if (this.#room.localParticipant.getTrackPublication(this.#kind))
        this.#waiting.add(identity);
      return this.#send(identity, { t: "no", sid, why: "full" });
    }

    // one direct copy per viewer, a new ask replaces the old one
    if (this.#out.has(identity)) this.#endOutgoing(identity, false);
    // one shared encode costs the same for any number of viewers
    if (h264Hardware() === false && !this.#sharedOn())
      this.#cap = MAX_DIRECT_SOFTWARE;
    // Konkord: free places go to the line in order, nobody jumps it
    if (!this.#mayEnter(identity)) {
      this.#waiting.add(identity);
      this.#send(identity, { t: "no", sid, why: "full" });
      this.#tellPlaces();
      return;
    }
    this.#waiting.delete(identity);
    this.#offeredAt.delete(identity);
    this.#toldPlace.delete(identity);
    this.#tellPlaces();

    // Konkord: the one shared encode when the browser can (Chromium)
    const shared = h264 ? this.#ensureShared(track) : undefined;
    const pc = new RTCPeerConnection(
      shared
        ? ({
            iceServers: ICE_SERVERS,
            encodedInsertableStreams: true,
          } as RTCConfiguration)
        : { iceServers: ICE_SERVERS },
    );
    const transceiver = pc.addTransceiver(shared ? shared.track : track, {
      direction: "sendonly",
    });
    if (shared) {
      // Konkord: the shared encode's own profile on offer (see keepAllH264)
      keepAllH264(pc);
      onlyH264(transceiver);
    } else preferH264(transceiver);

    const out: Outgoing = {
      sid,
      identity,
      pc,
      sender: transceiver.sender,
      sounds: [],
      soundWork: Promise.resolve(),
      shared: shared?.attach(transceiver.sender),
      connected: false,
      candidates: 0,
      timer: setTimeout(() => {
        if (!out.connected) this.#endOutgoing(identity, true);
      }, CONNECT_TIMEOUT_MS),
    };
    this.#out.set(identity, out);

    pc.onicecandidate = (event) => {
      const c = event.candidate && shareableCandidate(event.candidate);
      if (c) this.#send(identity, { t: "ice", sid, c });
    };
    pc.onconnectionstatechange = () => {
      if (this.#out.get(identity) !== out) return;
      if (pc.connectionState === "connected") {
        out.connected = true;
        out.connectedAt ??= Date.now();
        void this.#applyEncoding(out);
      } else if (pc.connectionState === "disconnected") {
        setTimeout(() => {
          if (
            this.#out.get(identity) === out &&
            pc.connectionState === "disconnected"
          )
            this.#endOutgoing(identity, true);
        }, DISCONNECTED_MS);
      } else if (
        pc.connectionState === "failed" ||
        pc.connectionState === "closed"
      ) {
        this.#endOutgoing(identity, true);
      }
    };

    // Konkord: a program starting now waits its turn, or two offers would cross
    const first = (async () => {
      // the stream's sounds are in the first offer
      await this.#placeSounds(out);
      await pc.setLocalDescription(await pc.createOffer());
      if (this.#out.get(identity) !== out) return;
      await this.#applyEncoding(out);
      this.#sendOffer(out);
      await this.#finishSounds(out);
    })();
    out.soundWork = first.catch(() => {});

    try {
      await first;
    } catch (err) {
      console.warn("[konkord] direct stream offer failed", err);
      if (this.#out.get(identity) === out) this.#endOutgoing(identity, true);
    }
  }

  #sendOffer(out: Outgoing) {
    this.#send(out.identity, {
      t: "offer",
      sid: out.sid,
      sdp: withoutCandidates(out.pc.localDescription!.sdp),
      from: [
        appName(),
        out.shared ? "shared" : "own",
        h264Hardware() === false ? "sw" : "hw",
      ].join("."),
    });
  }

  /**
   * Konkord: this stream's sounds, sent to nobody through the server
   */
  #soundSources() {
    const sources: { name: string; track: MediaStreamTrack }[] = [];
    if (this.#kind !== Track.Source.ScreenShare || !this.#directSound())
      return sources;
    for (const publication of this.#room.localParticipant.trackPublications.values()) {
      const track = publication.track;
      if (
        publication.source !== Track.Source.ScreenShareAudio ||
        !(track instanceof LocalAudioTrack)
      )
        continue;
      if (!track.isUpstreamPaused) void track.pauseUpstream().catch(() => {});
      // swapping the track (another screen) puts it back on the server's sender
      else if (track.sender?.track)
        void track.sender.replaceTrack(null).catch(() => {});
      if (track.mediaStreamTrack.readyState === "live")
        sources.push({
          name: publication.trackName,
          track: track.mediaStreamTrack,
        });
    }
    return sources.slice(0, MAX_STREAM_SOUNDS);
  }

  /**
   * Konkord: each sound on its place of this connection; true when a new
   * place was added, which takes another offer
   */
  async #placeSounds(out: Outgoing) {
    const sources = this.#soundSources();
    const onSlot = (slot: SoundSlot) => slot.transceiver.sender.track;
    const free = out.sounds.filter(
      (slot) => !sources.some((source) => source.track === onSlot(slot)),
    );
    let added = false;
    for (const source of sources) {
      const placed = out.sounds.find((slot) => onSlot(slot) === source.track);
      if (placed) {
        placed.name = source.name;
        continue;
      }
      const slot = free.shift();
      if (slot) {
        await slot.transceiver.sender.replaceTrack(source.track);
        slot.name = source.name;
        continue;
      }
      const transceiver = out.pc.addTransceiver(source.track, {
        direction: "sendonly",
      });
      // a connection with the shared encode passes on every frame by hand
      if (out.shared) passFramesOn(transceiver.sender);
      out.sounds.push({ transceiver, name: source.name });
      added = true;
    }
    for (const slot of free) {
      await slot.transceiver.sender.replaceTrack(null);
      slot.name = undefined;
    }
    return added;
  }

  /**
   * Konkord: the quality of new sounds, and which sound is which
   */
  async #finishSounds(out: Outgoing) {
    for (const slot of out.sounds) {
      if (slot.tuned || slot.name === undefined) continue;
      const parameters = slot.transceiver.sender.getParameters();
      if (!parameters.encodings?.length) continue;
      parameters.encodings[0].maxBitrate = STREAM_SOUND_BITRATE;
      await slot.transceiver.sender.setParameters(parameters).catch(() => {});
      slot.tuned = true;
    }
    const list = out.sounds.flatMap((slot) =>
      slot.name !== undefined && slot.transceiver.mid
        ? [{ mid: slot.transceiver.mid, name: slot.name }]
        : [],
    );
    const told = JSON.stringify(list);
    if (told === (out.soundsTold ?? "[]")) return;
    out.soundsTold = told;
    this.#send(out.identity, { t: "sounds", sid: out.sid, list });
  }

  #queueSounds(out: Outgoing) {
    out.soundWork = out.soundWork
      .then(async () => {
        if (this.#out.get(out.identity) !== out) return;
        if (await this.#placeSounds(out)) await this.#offerAgain(out);
        await this.#finishSounds(out);
      })
      .catch((err) =>
        console.warn("[konkord] stream sound change failed", err),
      );
  }

  async #offerAgain(out: Outgoing) {
    if (out.pc.signalingState !== "stable") {
      out.reoffer = true;
      return;
    }
    await out.pc.setLocalDescription(await out.pc.createOffer());
    if (this.#out.get(out.identity) !== out) return;
    this.#sendOffer(out);
    this.#awaitAnswer(out);
  }

  /**
   * Konkord: an offer lost on the way (the call reconnecting) would leave every
   * later program silent: send it again, and in the end start the link over
   */
  #awaitAnswer(out: Outgoing, tries = 0) {
    clearTimeout(out.answerTimer);
    out.answerTimer = setTimeout(() => {
      if (
        this.#out.get(out.identity) !== out ||
        out.pc.signalingState !== "have-local-offer"
      )
        return;
      if (tries >= OFFER_RETRIES) return this.#endOutgoing(out.identity, true);
      this.#sendOffer(out);
      this.#awaitAnswer(out, tries + 1);
    }, OFFER_RETRY_MS);
  }

  async #onAnswer(identity: string, message: { sid: string; sdp: string }) {
    const out = this.#out.get(identity);
    if (!out || out.sid !== message.sid || typeof message.sdp !== "string")
      return;
    // the second answer to an offer that was sent twice
    if (out.pc.signalingState !== "have-local-offer") return;
    try {
      await out.pc.setRemoteDescription({
        type: "answer",
        sdp: withoutCandidates(message.sdp),
      });
      if (out.reoffer) {
        out.reoffer = false;
        out.soundWork = out.soundWork
          .then(async () => {
            if (this.#out.get(identity) !== out) return;
            await this.#offerAgain(out);
            await this.#finishSounds(out);
          })
          .catch((err) =>
            console.warn("[konkord] stream sound change failed", err),
          );
      }
    } catch (err) {
      console.warn("[konkord] direct stream answer rejected", err);
      this.#endOutgoing(identity, true);
    }
  }

  #onSize(identity: string, message: { sid: string; h: number }) {
    const out = this.#out.get(identity);
    if (!out || out.sid !== message.sid || !Number.isFinite(message.h)) return;
    // Konkord: 0 = they show it nowhere right now, stop encoding it for them
    const paused = message.h <= 0;
    // a pause or resume still being applied must not hide this one
    if (paused !== !!out.paused) out.applied = undefined;
    out.paused = paused;
    if (!out.paused) out.height = Math.max(144, Math.min(4320, message.h));
    else out.sample = undefined;
    void this.#applyEncoding(out);
  }

  /**
   * Same bitrate and frame rate as the server copy, smaller when the viewer's
   * tile is small (less upload, less encoder work)
   */
  #sharedOn() {
    return !!this.#shared || (!this.#sharedBroken && sharedEncodeSupported());
  }

  /**
   * Konkord: the shared encode, started with the first direct viewer
   */
  #ensureShared(track: MediaStreamTrack) {
    if (this.#sharedBroken || !sharedEncodeSupported()) return;
    if (!this.#shared) {
      const format = this.#format();
      this.#shared = new SharedEncoder(
        track,
        {
          height: track.getSettings().height ?? 1080,
          fps: format?.fps ?? 30,
          bitrate: format?.bitrate ?? 4_000_000,
        },
        (error) => {
          // back to one encode per viewer: they ask again and get that
          console.warn("[konkord] shared encode failed, one per viewer", error);
          this.#sharedBroken = true;
          this.#shared = undefined;
          for (const out of [...this.#out.values()])
            if (out.shared) this.#endOutgoing(out.identity, true);
        },
      );
    }
    return this.#shared;
  }

  /**
   * Konkord: the shared encode at the biggest size a viewer shows, the
   * frame rate of the stream, and a bitrate the slowest link carries
   */
  #updateShared() {
    const shared = this.#shared;
    const target = this.#sharedTarget();
    if (!shared || !target) return;
    let { bitrate } = target;
    // the slowest link: a little under what it carries (links too slow for
    // the shared encode have their own copy and don't count, see SLOW_LINK)
    const links = [...this.#out.values()]
      .filter(
        (out) =>
          out.shared &&
          !out.paused &&
          !out.own &&
          Date.now() >= (out.trialUntil ?? 0) &&
          this.#graceOver(out),
      )
      .map((out) => out.available)
      .filter((value): value is number => !!value);
    // Konkord: sending under the estimate keeps the estimate down (it only grows
    // with what it sees go through, and stayed low long after a spike ended), so
    // with nothing lost lately it is tried a little above; a link that can't take
    // it loses packets, the bitrate is back under it within seconds and the next
    // try waits twice as long (see #noteLoss)
    const calm =
      this.#kind === Track.Source.ScreenShare &&
      this.#fitOn() &&
      Date.now() - this.#lossAt >= this.#lossWait;
    if (links.length)
      bitrate = Math.max(
        MIN_BITRATE,
        Math.min(bitrate, Math.min(...links) * (calm ? 1.1 : 0.85)),
      );
    void shared.update({ ...target, bitrate: Math.round(bitrate) });
  }

  /**
   * Konkord: what the shared encode aims for before the links: the biggest
   * size a viewer on it shows, the stream's frame rate and the bitrate for
   * that size (undefined with nobody on it)
   */
  #sharedTarget(including?: Outgoing) {
    // Konkord: a camera published without a bitrate still has a sensible one
    const format =
      this.#format() ??
      (this.#kind === Track.Source.Camera
        ? { bitrate: 3_000_000, fps: 30 }
        : undefined);
    const source = this.#source();
    if (!this.#shared || !format || !source) return;
    const camera = this.#kind === Track.Source.Camera;
    // Konkord: a camera's own size follows what is wanted (see
    // #captureCamera), so the top it can reach is Full HD, not its current size
    const sourceHeight = camera
      ? CAMERA_MAX_HEIGHT
      : (source.getSettings().height ?? 1080);
    const sourceWidth = camera
      ? (CAMERA_MAX_HEIGHT * 16) / 9
      : (source.getSettings().width ?? (sourceHeight * 16) / 9);
    const watching = [...this.#out.values()].filter(
      (out) => out.shared && !out.paused && (!out.own || out === including),
    );
    if (!watching.length) return;
    const wanted = Math.max(
      ...watching.map((out) =>
        out.height
          ? (HEIGHT_STEPS.find((step) => step >= out.height!) ?? sourceHeight)
          : sourceHeight,
      ),
    );
    const height = Math.min(
      wanted,
      sourceHeight,
      this.#fitted()?.height ?? Infinity,
    );
    if (camera) this.#captureCamera(source, height);
    const scale = sourceHeight / height;
    const pixels = (sourceWidth / scale) * height;
    const bitrate = Math.min(
      MAX_DIRECT_BITRATE,
      Math.max(
        MIN_BITRATE,
        format.bitrate / (scale * scale),
        pixels * format.fps * DIRECT_BITS_PER_PIXEL,
      ),
    );
    return { height, fps: format.fps, bitrate };
  }

  /**
   * Konkord: once per check, whether a viewer's link carries the shared
   * encode; a slow one gets its own copy (adapted to its link by the
   * browser) and goes back to the shared one when its link recovers
   */
  async #checkLink(out: Outgoing) {
    if (!out.shared || out.paused || !out.available) return;
    if (!this.#graceOver(out)) return;
    // an own copy is measured against the shared encode with its tile in
    // it (what it would get back)
    const target = this.#sharedTarget(out.own ? out : undefined);
    if (!target && !out.own) return;
    // with only own copies left, the shared encode aims at the stream's size
    const wanted = target?.bitrate ?? this.#format()?.bitrate;
    if (!wanted) return;
    if (!out.own) {
      // Konkord: a try on the shared encode: its estimate is still climbing
      if (Date.now() < (out.trialUntil ?? 0)) return;
      out.slowChecks =
        out.available < wanted * SLOW_LINK ? (out.slowChecks ?? 0) + 1 : 0;
      // Konkord: everyone slow at once is the streamer's upload, not their
      // links: an encode for each would only make it worse (see StreamFit)
      const links = [...this.#out.values()].filter(
        (other) =>
          other.shared &&
          !other.paused &&
          !other.own &&
          Date.now() >= (other.trialUntil ?? 0),
      );
      const upload =
        this.#kind === Track.Source.ScreenShare &&
        this.#fitOn() &&
        links.length > 1 &&
        links.every((other) => (other.available ?? 0) < wanted * SLOW_LINK);
      if (out.slowChecks >= SLOW_CHECKS && !upload)
        await this.#setOwn(out, true);
    } else {
      if (
        this.#kind === Track.Source.ScreenShare &&
        this.#fitOn() &&
        Date.now() >= (out.ownTrialAt ?? Infinity) &&
        Date.now() >= (out.ownUntil ?? 0)
      ) {
        out.trialAt = Date.now();
        out.trialUntil = Date.now() + OWN_TRIAL_GRACE_MS;
        await this.#setOwn(out, false);
        return;
      }
      out.fastChecks =
        out.available >= wanted * FAST_LINK ? (out.fastChecks ?? 0) + 1 : 0;
      if (out.fastChecks >= FAST_CHECKS && Date.now() >= (out.ownUntil ?? 0))
        await this.#setOwn(out, false);
    }
  }

  /**
   * Konkord: a viewer whose picture is failing. Its own copy follows its link
   * right away (the browser adapts it), where the shared encode's frames are too
   * big for it. Not more than a few: each is another encode on this PC.
   */
  async #onSlow(identity: string, sid: string) {
    const out = this.#out.get(identity);
    if (!out || out.sid !== sid || !out.shared || out.own || out.paused) return;
    const copies = [...this.#out.values()].filter((other) => other.ownUntil);
    if (copies.length >= MAX_ASKED_COPIES) return;
    // Konkord: everyone's picture failing together is the streamer's upload,
    // not their links: an encode for each would only send more. A moment to
    // hear from the others, then the shared encode's bitrate is the answer.
    out.slowAt = Date.now();
    const others = () =>
      [...this.#out.values()].filter(
        (other) => other !== out && other.shared && !other.own && !other.paused,
      );
    if (
      this.#kind === Track.Source.ScreenShare &&
      this.#fitOn() &&
      others().length &&
      Date.now() >= (out.trialUntil ?? 0)
    ) {
      if (Date.now() < this.#uploadTroubleUntil) return;
      await new Promise((resolve) => setTimeout(resolve, UPLOAD_WAIT_MS));
      if (this.#out.get(identity) !== out || out.sid !== sid || out.own) return;
      if (Date.now() < this.#uploadTroubleUntil) return;
      const heard = Date.now() - UPLOAD_HEARD_MS;
      if (others().every((other) => (other.slowAt ?? 0) >= heard)) {
        this.#uploadTroubleUntil = Date.now() + UPLOAD_TROUBLE_MS;
        return;
      }
    }
    out.ownUntil = Date.now() + ASKED_COPY_MS;
    await this.#setOwn(out, true);
  }

  async #setOwn(out: Outgoing, own: boolean) {
    const source = this.#source();
    const shared = this.#shared;
    if (!out.shared || !source || !shared) return;
    try {
      await out.sender.replaceTrack(own ? source : shared.track);
      // a new screen picked meanwhile
      const now = this.#source();
      if (own && now && now !== source) await out.sender.replaceTrack(now);
    } catch {
      return;
    }
    if (this.#out.get(out.identity) !== out) return;
    out.own = own;
    if (!own) out.ownUntil = undefined;
    else {
      // back too soon after a try: the link isn't ready, a longer wait
      const failed = Date.now() - (out.trialAt ?? -Infinity) < OWN_PROVED_MS;
      out.ownTrialWait = failed
        ? Math.min((out.ownTrialWait ?? OWN_TRIAL_MS) * 2, OWN_TRIAL_MAX_MS)
        : OWN_TRIAL_MS;
      out.ownTrialAt = Date.now() + out.ownTrialWait;
      out.trialUntil = undefined;
    }
    out.shared.setOwn(own);
    out.slowChecks = 0;
    out.fastChecks = 0;
    out.applied = undefined;
    console.info(
      `[konkord] direct copy for ${out.identity}: ${own ? "its own (slow link)" : "shared again"}`,
    );
    void this.#applyEncoding(out);
  }

  /**
   * Konkord: capture the camera at the size wanted (a moment later, and
   * only when it changes)
   */
  #captureCamera(source: MediaStreamTrack, wanted: number) {
    const height = Math.max(
      CAMERA_MIN_HEIGHT,
      Math.min(CAMERA_MAX_HEIGHT, wanted),
    );
    if (height === this.#captureHeight) {
      clearTimeout(this.#captureTimer);
      this.#captureTimer = undefined;
      return;
    }
    // a tile resized while waiting: the latest size wins, not the first
    this.#captureWanted = height;
    if (this.#captureTimer) return;
    this.#captureTimer = setTimeout(() => {
      this.#captureTimer = undefined;
      const next = this.#captureWanted;
      if (
        this.#closed ||
        source.readyState !== "live" ||
        next === this.#captureHeight
      )
        return;
      this.#captureHeight = next;
      void source
        .applyConstraints({
          width: { ideal: Math.round((next * 16) / 9) },
          height: { ideal: next },
          frameRate: { ideal: 30, max: 30 },
        })
        .catch(() => {});
    }, CAMERA_RESIZE_DELAY_MS);
  }

  async #applyEncoding(out: Outgoing) {
    // Konkord: the shared encode decides size and bitrate for everyone; the
    // connection itself only carries it (no limit of its own)
    if (out.shared) out.shared.setPaused(!!out.paused);
    if (out.shared && !out.own) {
      // Konkord: room for extra stand-in frames (a lost one is made up
      // right away) and never fewer frames: each stand-in carries a real one
      const fps = this.#format()?.fps ?? 60;
      const key = `shared|${fps}`;
      if (out.applied !== key) {
        const params = out.sender.getParameters();
        if (params.encodings?.length) {
          params.encodings[0].active = true;
          params.encodings[0].maxBitrate = MAX_DIRECT_BITRATE * 2;
          params.encodings[0].scaleResolutionDownBy = 1;
          params.encodings[0].maxFramerate = fps * 2;
          (params as { degradationPreference?: string }).degradationPreference =
            "maintain-framerate";
          try {
            await out.sender.setParameters(params);
            out.applied = key;
          } catch {
            // not negotiated yet, applied again on the next check
          }
        }
      }
      this.#updateShared();
      return;
    }
    if (out.paused) {
      if (out.applied === "paused") return;
      const params = out.sender.getParameters();
      if (!params.encodings?.length) return;
      params.encodings[0].active = false;
      try {
        await out.sender.setParameters(params);
        if (out.paused) out.applied = "paused";
      } catch {
        // applied again on the next check
      }
      return;
    }

    const format = this.#format();
    const sourceHeight = out.sender.track?.getSettings().height;
    if (!format || !sourceHeight) return;

    const shown = out.height
      ? (HEIGHT_STEPS.find((step) => step >= out.height!) ?? sourceHeight)
      : sourceHeight;
    // Konkord: a slow link's own copy stays small (it can't carry more)
    const fitOn = this.#kind === Track.Source.ScreenShare && this.#fitOn();
    const wanted = Math.min(
      out.own
        ? fitOn
          ? Math.min(shown, this.#ownHeight(out, sourceHeight))
          : Math.min(shown, SLOW_LINK_HEIGHT)
        : shown,
      this.#fitted()?.height ?? Infinity,
    );
    const scale = Math.max(1, sourceHeight / Math.min(wanted, sourceHeight));
    const sourceWidth =
      out.sender.track?.getSettings().width ?? (sourceHeight * 16) / 9;
    const pixels = (sourceWidth / scale) * (sourceHeight / scale);
    const maxBitrate = Math.round(
      Math.min(
        MAX_DIRECT_BITRATE,
        Math.max(
          MIN_BITRATE,
          format.bitrate / (scale * scale),
          pixels * format.fps * DIRECT_BITS_PER_PIXEL,
        ),
      ),
    );
    const key = `${maxBitrate}|${scale}|${format.fps}`;
    if (out.applied === key) return;

    const params = out.sender.getParameters();
    if (!params.encodings?.length) return;
    params.encodings[0].active = true;
    params.encodings[0].maxBitrate = maxBitrate;
    params.encodings[0].scaleResolutionDownBy = scale;
    params.encodings[0].maxFramerate = format.fps;
    // Konkord: an own copy (slow link) rides a connection agreed on
    // Constrained Baseline first (the shared encode's profile), which
    // Chromium encodes on the processor; Main / High go to the GPU, when
    // the viewer took them too (Safari doesn't)
    const gpu = out.own ? gpuH264Codec(params) : undefined;
    if (gpu)
      (
        params.encodings[0] as RTCRtpEncodingParameters & {
          codec?: RTCRtpCodecParameters;
        }
      ).codec = gpu;
    // Konkord: left to itself the browser shrinks the picture to almost nothing
    // at the first trouble (180p): the size follows the link in #ownHeight
    // instead, and frames give way first
    (params as { degradationPreference?: string }).degradationPreference =
      fitOn && out.own ? "maintain-resolution" : "maintain-framerate";
    try {
      await out.sender.setParameters(params);
      if (!out.paused) out.applied = key;
    } catch {
      // Konkord: a browser without codec choice per encoding: once more
      // without it; else not negotiated yet (applied on the next check)
      if (gpu) {
        delete (params.encodings[0] as { codec?: unknown }).codec;
        await out.sender
          .setParameters(params)
          .then(() => {
            if (!out.paused) out.applied = key;
          })
          .catch(() => {});
      }
    }
  }

  #endOutgoing(identity: string, tell: boolean) {
    const out = this.#out.get(identity);
    if (!out) return;
    this.#out.delete(identity);
    clearTimeout(out.timer);
    out.shared?.detach();
    out.pc.onconnectionstatechange = null;
    out.pc.close();
    // nobody left on the shared encode: stop encoding
    if (this.#shared && !this.#shared.viewers) {
      this.#shared.close();
      this.#shared = undefined;
    }
    if (tell) this.#send(identity, { t: "bye", sid: out.sid });
    this.#offerSlots();
    this.#tellPlaces();
  }

  /**
   * Konkord: the line for a place, in order, without those who left the call
   */
  #line() {
    for (const identity of [...this.#waiting])
      if (!this.#room.remoteParticipants.has(identity))
        this.#waiting.delete(identity);
    return [...this.#waiting];
  }

  /**
   * Konkord: whether someone asking now gets a place: the first in line get
   * the free ones, a newcomer only places nobody in line is waiting for
   */
  #mayEnter(identity: string) {
    const free = this.#limit() - this.#out.size;
    if (free <= 0) return false;
    const line = this.#line();
    const at = line.indexOf(identity);
    return at === -1 ? line.length < free : at < free;
  }

  /**
   * Free direct places: offer them to the first in line (they ask then);
   * one who doesn't ask within SLOT_HOLD_MS leaves the line
   */
  #offerSlots() {
    if (!this.#source()) return;
    const now = Date.now();
    for (const [identity, at] of [...this.#offeredAt]) {
      if (now - at < SLOT_HOLD_MS) continue;
      this.#offeredAt.delete(identity);
      this.#waiting.delete(identity);
      this.#toldPlace.delete(identity);
    }
    const free = this.#limit() - this.#out.size;
    for (const identity of this.#line().slice(0, Math.max(0, free))) {
      if (this.#offeredAt.has(identity)) continue;
      this.#offeredAt.set(identity, now);
      this.#send(identity, { t: "slot" });
    }
  }

  /**
   * Konkord: tell each one in line their place when it changed
   */
  #tellPlaces() {
    const line = this.#line();
    line.forEach((identity, i) => {
      if (this.#toldPlace.get(identity) === i + 1) return;
      this.#toldPlace.set(identity, i + 1);
      this.#send(identity, { t: "queue", pos: i + 1 });
    });
    for (const identity of [...this.#toldPlace.keys()])
      if (!this.#waiting.has(identity)) this.#toldPlace.delete(identity);
  }

  /**
   * Konkord: more direct viewers than places (the streamer lowered the
   * limit, or the computer struggles): the last ones to start watching are
   * told the stream is full and wait first in line, in their order
   */
  #trimToLimit() {
    const over = [...this.#out.values()].slice(this.#limit());
    if (!over.length) return;
    for (const out of over) {
      this.#send(out.identity, { t: "no", sid: out.sid, why: "full" });
      this.#endOutgoing(out.identity, false);
    }
    this.#waiting = new Set([
      ...over.map((out) => out.identity),
      ...this.#waiting,
    ]);
    this.#tellPlaces();
  }

  /**
   * Konkord: the size of an own copy: the biggest its link carries at 24 frames.
   * Down when it's clearly too much, up only with room to spare, so it doesn't
   * keep changing.
   */
  #ownHeight(out: Outgoing, sourceHeight: number) {
    const heights = HEIGHT_STEPS.filter((step) => step <= sourceHeight);
    const bits = (height: number) =>
      ((height * 16) / 9) * height * MIN_FPS * DIRECT_BITS_PER_PIXEL;
    const available = out.available ?? 0;
    let height = out.ownHeight ?? Math.min(SLOW_LINK_HEIGHT, sourceHeight);
    while (height > heights[0] && available < bits(height) * 0.7)
      height = heights[heights.indexOf(height) - 1] ?? heights[0];
    const up = heights[heights.indexOf(height) + 1];
    if (up && available >= bits(up) * 1.2) height = up;
    out.ownHeight = height;
    return height;
  }

  /** Konkord: a link lost packets: no trying above the estimate for a while */
  /**
   * Konkord: a new connection's estimate starts low and climbs, so it isn't
   * believed for a while; a link already losing packets is really short, and
   * waiting froze the picture for the first 25 s on a weak upload
   */
  #graceOver(out: Outgoing) {
    return (
      Date.now() - (out.connectedAt ?? Date.now()) >= LINK_GRACE_MS ||
      out.lostAt !== undefined
    );
  }

  #noteLoss() {
    const now = Date.now();
    const since = now - this.#lossAt;
    if (since >= LOSS_FORGOTTEN_MS) this.#lossWait = LOSS_CALM_MS;
    // it had waited and tried: it's the link's limit, not a moment
    else if (since >= this.#lossWait)
      this.#lossWait = Math.min(this.#lossWait * 2, LOSS_CALM_MAX_MS);
    this.#lossAt = now;
  }

  /**
   * Konkord: what the links carry against what the stream needs. Only links on
   * the shared encode tell about the upload: an own copy's estimate stays as low
   * as its small picture, whatever the upload (and it adapts by itself). A link
   * that just connected isn't asked: its estimate starts low.
   */
  #checkFit() {
    if (this.#kind !== Track.Source.ScreenShare || !this.#fitOn()) return;
    const picked = this.#picked();
    const source = this.#source();
    if (!picked || !source) return;
    const sourceHeight = source.getSettings().height ?? 1080;
    const sourceWidth = source.getSettings().width ?? (sourceHeight * 16) / 9;
    const links = [...this.#out.values()].filter(
      (out) =>
        out.connected &&
        !out.paused &&
        !!out.available &&
        out.shared &&
        !out.own &&
        Date.now() >= (out.trialUntil ?? 0) &&
        this.#graceOver(out),
    );
    if (!links.length) return;
    const carry = Math.min(...links.map((out) => out.available!));
    const shown = Math.max(
      ...links.map((out) =>
        out.height
          ? (HEIGHT_STEPS.find((step) => step >= out.height!) ?? sourceHeight)
          : sourceHeight,
      ),
    );

    const before = this.#fit.level;
    const level = this.#fit.check(
      { height: sourceHeight, fps: picked.fps },
      carry,
      ({ height, fps }) => {
        const scale = sourceHeight / Math.min(height, shown, sourceHeight);
        const pixels = (sourceWidth / scale) * (sourceHeight / scale);
        return Math.min(
          MAX_DIRECT_BITRATE,
          Math.max(
            MIN_BITRATE,
            (picked.bitrate * fps) / picked.fps / (scale * scale),
            pixels * fps * DIRECT_BITS_PER_PIXEL,
          ),
        );
      },
      Date.now(),
      Date.now() - this.#lossAt < FIT_LOSSY_MS,
    );
    if (!before || before === level) return;
    console.info(
      `[konkord] stream fit: ${level.height}p ${level.fps} FPS (links carry ${Math.round(carry / 1000)} kbps)`,
    );
    this.#onFit?.(this.#fit.lowered ? level : undefined);
  }

  /**
   * Watch how the direct copies are doing: fewer places when the encoder or
   * the upload can't keep up, one place without a hardware encoder
   */
  async #checkSending() {
    if (this.#closing || this.#checking) return;

    // turned off in the settings: everyone goes back to the server
    if (!this.#enabled()) {
      for (const identity of [...this.#out.keys()])
        this.#endOutgoing(identity, true);
      for (const [identity, entry] of [...this.#in]) {
        this.#send(identity, { t: "bye", sid: entry.sid });
        this.#dropIncoming(identity, entry.sid, true);
      }
    }

    if (!this.#source()) {
      if (this.sending()) this.#setSending();
      return;
    }

    this.#checking = true;
    try {
      await this.#measureSending();
    } finally {
      this.#checking = false;
    }
    this.#offerSlots();
  }

  async #measureSending() {
    let encoderName: string | undefined;
    let hardware: boolean | undefined;
    let reason = "none";
    // Konkord: the shared encode dropping frames means the computer can't
    // keep up (the connections themselves only encode a stand-in)
    this.#sharedStats = this.#shared?.stats();
    if (this.#sharedStats) {
      encoderName = "shared (WebCodecs)";
      if (this.#sharedStats.dropped > this.#sharedStats.fps * 0.1)
        reason = "cpu";
    }
    const copies: DirectCopy[] = [];
    for (const out of [...this.#out.values()]) {
      if (!out.connected) continue;
      void this.#applyEncoding(out);
      // paused (shown nowhere): nothing to measure, keeps its place
      if (out.paused) continue;
      const report = await out.sender.getStats().catch(() => undefined);
      // Konkord: what each link carries, for the shared encode's bitrate
      report?.forEach((stat) => {
        if (
          stat.type === "candidate-pair" &&
          stat.nominated &&
          typeof stat.availableOutgoingBitrate === "number"
        )
          // Konkord: alt-tab drops the estimate for no real reason, see
          // holdEstimate
          out.available =
            this.#kind === Track.Source.ScreenShare && this.#fitOn()
              ? holdEstimate(
                  (out.heldEstimate ??= {}),
                  stat.availableOutgoingBitrate,
                  Date.now(),
                  Date.now() - this.#lossAt < DIP_HOLD_MS,
                )
              : stat.availableOutgoingBitrate;
        if (
          stat.type === "remote-inbound-rtp" &&
          stat.kind === "video" &&
          (stat.fractionLost ?? 0) > LOSS_FRACTION
        )
          {
            this.#noteLoss();
            out.lostAt = Date.now();
          }
      });
      await this.#checkLink(out);
      report?.forEach((stat) => {
        if (stat.type !== "outbound-rtp" || stat.kind !== "video") return;
        // Konkord: the shared encode's own numbers (the connection only
        // encodes a tiny stand-in)
        if (out.shared && !out.own) {
          const sample: SendSample = {
            at: stat.timestamp,
            bytes: stat.bytesSent ?? 0,
            encodeTime: 0,
            frames: stat.framesEncoded ?? 0,
          };
          const rates = sendRates(out.sample, sample);
          copies.push({
            identity: out.identity,
            available: out.available,
            own: !!out.own,
            width: this.#sharedStats?.width ?? 0,
            height: this.#sharedStats?.height ?? 0,
            fps: this.#sharedStats?.fps ?? 0,
            limit: "shared",
            ...rates,
            encodeMs: this.#sharedStats?.encodeMs ?? rates.encodeMs,
          });
          out.sample = sample;
          return;
        }
        encoderName ??= stat.encoderImplementation;
        if (typeof stat.powerEfficientEncoder === "boolean")
          hardware = (hardware ?? true) && stat.powerEfficientEncoder;
        const limit = stat.qualityLimitationReason;
        // Konkord: an own copy (slow link) is limited by that link: not a
        // reason to take places from everyone
        if (limit && limit !== "none" && reason !== "cpu" && !out.own)
          reason = limit;

        const sample: SendSample = {
          at: stat.timestamp,
          bytes: stat.bytesSent ?? 0,
          encodeTime: stat.totalEncodeTime ?? 0,
          frames: stat.framesEncoded ?? 0,
        };
        copies.push({
          identity: out.identity,
          available: out.available,
          own: !!out.own,
          width: stat.frameWidth ?? 0,
          height: stat.frameHeight ?? 0,
          fps: Math.round(stat.framesPerSecond ?? 0),
          limit: limit ?? "none",
          ...sendRates(out.sample, sample),
        });
        out.sample = sample;
      });
    }
    if (this.#closed) return;

    const connected = [...this.#out.values()].filter((out) => out.connected);
    if (hardware === false && !this.#sharedOn())
      this.#cap = MAX_DIRECT_SOFTWARE;

    // the newest direct viewer goes back to the server when the computer or
    // the upload is struggling for a few seconds
    // paused copies (shown nowhere) cost nothing: not counted as load
    const active = connected.filter((out) => !out.paused);
    this.#checkFit();
    // Konkord: a tight upload first lowers the stream, nobody loses their place
    const upload =
      reason === "bandwidth" &&
      (this.#kind !== Track.Source.ScreenShare ||
        !this.#fitOn() ||
        this.#fit.atFloor);
    this.#pressure =
      (reason === "cpu" || upload) && active.length > 1
        ? this.#pressure + 1
        : 0;
    if (this.#pressure >= 2) {
      this.#pressure = 0;
      this.#calm = 0;
      this.#cap = Math.max(1, connected.length - 1);
    }
    // a place taken away comes back after a calm half minute
    const most =
      !this.#sharedOn() && (hardware === false || h264Hardware() === false)
        ? MAX_DIRECT_SOFTWARE
        : this.#hardwareMax();
    if (reason === "none" && this.#cap < most) {
      if (++this.#calm >= CALM_CHECKS) {
        this.#calm = 0;
        this.#cap++;
      }
    } else if (reason !== "none") {
      this.#calm = 0;
    }
    this.#trimToLimit();
    // Konkord: places that came back (or were held too long) go to the line
    this.#offerSlots();

    this.#setSending({
      viewers: connected.length,
      limit: this.#limit(),
      waiting: this.#line().length,
      encoder: encoderName,
      hardware,
      reason,
      copies,
    });
  }

  // --- viewer side ---

  async #onOffer(
    identity: string,
    message: { sid: string; sdp: string; from?: unknown },
  ) {
    const entry = this.#in.get(identity);
    if (!entry || entry.sid !== message.sid || typeof message.sdp !== "string")
      return;
    if (
      typeof message.from === "string" &&
      /^[a-z0-9.]{1,32}$/.test(message.from)
    )
      entry.streamer = message.from;
    try {
      await entry.pc.setRemoteDescription({
        type: "offer",
        sdp: withoutCandidates(message.sdp),
      });
      entry.remoteSet = true;
      for (const candidate of entry.queued.splice(0))
        await entry.pc.addIceCandidate(candidate).catch(() => {});
      const answer = await entry.pc.createAnswer();
      const sdp = stereoSound(answer.sdp ?? "");
      // Konkord: Firefox takes no changed answer, but plays stereo as it comes
      await entry.pc
        .setLocalDescription({ type: "answer", sdp })
        .catch(() => entry.pc.setLocalDescription(answer));
      this.#send(identity, {
        t: "answer",
        sid: entry.sid,
        sdp: withoutCandidates(sdp),
      });
    } catch (err) {
      console.warn("[konkord] direct stream offer rejected", err);
      this.#dropIncoming(identity, entry.sid, true, "error");
    }
  }

  /**
   * Konkord: which sound is which, from the streamer of this link
   */
  #onSounds(identity: string, message: { sid: string; list?: unknown }) {
    const entry = this.#in.get(identity);
    if (entry?.sid !== message.sid || !Array.isArray(message.list)) return;
    entry.soundNames = (message.list as unknown[])
      .slice(0, MAX_STREAM_SOUNDS)
      .flatMap((item) => {
        const sound = item as { mid?: unknown; name?: unknown } | null;
        return typeof sound?.mid === "string" &&
          sound.mid.length <= 8 &&
          typeof sound.name === "string" &&
          sound.name.length <= 200
          ? [{ mid: sound.mid, name: sound.name }]
          : [];
      });
    this.#showSounds(entry);
  }

  #showSounds(entry: Incoming) {
    if (this.#in.get(entry.identity) !== entry) return;
    const next = new Map(this.#sounds());
    next.set(
      entry.identity,
      entry.soundNames.flatMap(({ mid, name }) => {
        const track = entry.soundTracks.get(mid);
        return track ? [{ identity: entry.identity, name, track }] : [];
      }),
    );
    this.#setSounds(next);
  }

  async #onIce(identity: string, message: { sid: string; c: unknown }) {
    const candidate = acceptableCandidate(message.c);
    if (!candidate) return;

    const out = this.#out.get(identity);
    if (out?.sid === message.sid) {
      if (++out.candidates > MAX_CANDIDATES) return;
      await out.pc.addIceCandidate(candidate).catch(() => {});
      return;
    }
    const entry = this.#in.get(identity);
    if (entry?.sid !== message.sid) return;
    if (++entry.candidates > MAX_CANDIDATES) return;
    if (candidate.candidate) {
      entry.remote.add(candidateKind(candidate.candidate));
      const address = publicAddress(candidate.candidate);
      if (address) entry.publicRemote.add(address);
    }
    if (entry.remoteSet)
      await entry.pc.addIceCandidate(candidate).catch(() => {});
    else if (entry.queued.length < 50) entry.queued.push(candidate);
  }

  /**
   * The direct picture counts once frames are decoded, the server copy is
   * dropped a moment later (after the tile switched over). From then on, video
   * that stops arriving sends us back to the server.
   */
  #waitForFrames(entry: Incoming) {
    clearInterval(entry.frameCheck);
    entry.frameCheck = setInterval(async () => {
      if (this.#in.get(entry.identity) !== entry) {
        return clearInterval(entry.frameCheck);
      }
      const report = await entry.receiver?.getStats().catch(() => undefined);
      let frames = 0;
      report?.forEach((stat) => {
        if (stat.type === "inbound-rtp" && stat.kind === "video")
          frames = stat.framesDecoded ?? 0;
      });
      if (!frames || this.#in.get(entry.identity) !== entry) return;

      clearInterval(entry.frameCheck);
      this.#watchForStall(entry);
      entry.timers.push(
        setTimeout(() => this.#fallbacks.delete(entry.identity), 60_000),
      );
      const streams = new Map(this.#streams());
      streams.set(entry.identity, entry.stream!);
      this.#setStreams(streams);
      this.#setWhy(entry.identity, undefined);
      if (!entry.reported) {
        entry.reported = true;
        this.#attempts.delete(entry.identity);
        this.#onResult?.(this.#result(entry));
      }
      // Konkord: shown nowhere (the viewer is on another screen of the app):
      // pause it unless a tile reports a size within PAUSE_AFTER_MS
      if (!this.#sizes.has(entry.identity))
        this.#sizes.set(entry.identity, new Map());
      this.#sendSize(entry.identity);

      entry.timers.push(
        setTimeout(() => {
          if (this.#in.get(entry.identity) !== entry) return;
          this.#setCovered(new Set([...this.#covered(), entry.identity]));
          this.#onChange();
        }, SWITCH_DELAY_MS),
      );
    }, FRAME_CHECK_MS);
  }

  /**
   * A direct stream we were watching broke: the server copy takes over, and
   * the direct one is asked for again after a while (longer each time)
   */
  #recover(identity: string) {
    if (this.#closing) return;
    const attempt = this.#fallbacks.get(identity) ?? 0;
    this.#fallbacks.set(identity, attempt + 1);
    const wait = RECOVER_MS[Math.min(attempt, RECOVER_MS.length - 1)];
    this.#retryAt.set(identity, Date.now() + wait);
    clearTimeout(this.#recoverTimers.get(identity));
    this.#recoverTimers.set(
      identity,
      setTimeout(() => {
        this.#recoverTimers.delete(identity);
        // timers and Date.now() can disagree by a moment, watch() must not
        // turn this ask away
        this.#retryAt.delete(identity);
        if (!this.#closing && !this.#in.has(identity))
          this.#onSlotHandler?.(identity);
      }, wait),
    );
  }

  #watchForStall(entry: Incoming) {
    let bytes = -1;
    let still = 0;
    let decoded = -1;
    let frozen = 0;
    let hintedAt = 0;
    entry.frameCheck = setInterval(async () => {
      if (this.#in.get(entry.identity) !== entry) {
        return clearInterval(entry.frameCheck);
      }
      // Konkord: paused on purpose (shown nowhere): no video is expected
      if (entry.paused) {
        still = 0;
        frozen = 0;
        bytes = -1;
        decoded = -1;
        return;
      }
      const report = await entry.receiver?.getStats().catch(() => undefined);
      let now = 0;
      let frames = 0;
      report?.forEach((stat) => {
        if (stat.type === "inbound-rtp" && stat.kind === "video") {
          now = stat.bytesReceived ?? 0;
          frames = stat.framesDecoded ?? 0;
        }
      });
      // video keeps coming but the picture is stuck (e.g. a lost key frame)
      frozen = now !== bytes && frames === decoded ? frozen + 1 : 0;
      still = now === bytes ? still + 1 : 0;
      if (
        (frozen >= FROZEN_HINT_CHECKS || still >= STILL_HINT_CHECKS) &&
        Date.now() - hintedAt > SLOW_HINT_MS
      ) {
        hintedAt = Date.now();
        this.#send(entry.identity, { t: "slow", sid: entry.sid });
      }
      bytes = now;
      decoded = frames;
      if (
        (still >= STALL_CHECKS || frozen >= FROZEN_CHECKS) &&
        this.#in.get(entry.identity) === entry
      ) {
        console.warn(
          `[konkord] direct stream ${still ? "stalled" : "frozen"}, back to the server`,
        );
        this.#send(entry.identity, { t: "bye", sid: entry.sid });
        this.#dropIncoming(
          entry.identity,
          entry.sid,
          true,
          still ? "stall" : "frozen",
        );
      }
    }, 1000);
  }

  /**
   * Konkord: which candidates connected, for the count of how links go
   */
  async #notePair(entry: Incoming) {
    const report = await entry.pc.getStats().catch(() => undefined);
    if (!report) return;
    let pairId: string | undefined;
    report.forEach((stat) => {
      if (stat.type === "transport" && stat.selectedCandidatePairId)
        pairId = stat.selectedCandidatePairId;
    });
    report.forEach((stat) => {
      if (
        !pairId &&
        stat.type === "candidate-pair" &&
        stat.nominated &&
        stat.state === "succeeded"
      )
        pairId = stat.id;
    });
    const pair = pairId ? report.get(pairId) : undefined;
    const side = (id?: string) => {
      const c = id ? report.get(id) : undefined;
      if (!c) return "unknown";
      const address = String(c.address ?? c.ip ?? "");
      return candidateKind(
        `x 1 ${c.protocol ?? "udp"} 1 ${address} 1 typ ${c.candidateType ?? "unknown"}`,
      );
    };
    if (pair)
      entry.pair = `${side(pair.localCandidateId)}>${side(pair.remoteCandidateId)}`;
  }

  /**
   * Konkord: the anonymous summary of a link (see DirectResult)
   */
  #result(entry: Incoming, stage?: DirectResult["stage"]): DirectResult {
    return {
      ok: !stage,
      stage,
      attempt: entry.attempt,
      local: [...entry.local].sort().join(","),
      remote: [...entry.remote].sort().join(","),
      pair: entry.pair,
      sameNetwork: [...entry.publicLocal].some((address) =>
        entry.publicRemote.has(address),
      ),
      ms: Date.now() - entry.started,
      streamer: entry.streamer,
      app: appName(),
    };
  }

  /**
   * Stop the direct copy. When it was being shown, the server copy comes back
   * first and the direct picture stays a moment longer.
   */
  #dropIncoming(
    identity: string,
    sid: string,
    fallback: boolean,
    why?: DirectResult["stage"],
  ) {
    const entry = this.#in.get(identity);
    if (!entry || entry.sid !== sid) return;
    const stage =
      why ??
      (!entry.remoteSet
        ? "signal"
        : !entry.connected
          ? "ice"
          : // this link's own pictures (an older link's may still show)
            !entry.reported
            ? "media"
            : "drop");
    this.#in.delete(identity);
    if (this.#sounds().has(identity)) {
      const sounds = new Map(this.#sounds());
      sounds.delete(identity);
      this.#setSounds(sounds);
    }
    // Konkord: a link that never showed pictures, not turned away: failed
    if (fallback && !entry.refused && !entry.reported) {
      entry.reported = true;
      this.#attempts.set(identity, entry.attempt + 1);
      this.#onResult?.(this.#result(entry, stage));
    } else if (fallback && !entry.refused && !this.#closing) {
      // Konkord: it worked, then broke (the picture freezing for viewers)
      this.#onResult?.({ ...this.#result(entry, stage), broke: true });
    }
    entry.timers.forEach(clearTimeout);
    clearInterval(entry.frameCheck);
    clearTimeout(entry.sizeTimer);
    if (fallback && !this.#closing && this.#wanted.has(identity)) {
      this.#setWhy(identity, "failed");
      this.#scheduleRetry(identity, "failed");
    }
    if (fallback) {
      this.#retryAt.set(identity, Date.now() + RETRY_MS);
      // it was on screen: the server copy takes over and the direct one is
      // asked for again later
      if (entry.stream && this.#streams().get(identity) === entry.stream)
        this.#recover(identity);
    }

    const covered = this.#covered().has(identity);
    if (covered) {
      const next = new Set(this.#covered());
      next.delete(identity);
      this.#setCovered(next);
      if (!this.#closing) this.#onChange();
    }

    const removePicture = () => {
      entry.pc.onconnectionstatechange = null;
      entry.pc.close();
      if (this.#streams().get(identity) !== entry.stream) return;
      const streams = new Map(this.#streams());
      streams.delete(identity);
      this.#setStreams(streams);
    };
    if (fallback && covered && !this.#closing) {
      // close() doesn't wait for it
      const run = () => {
        this.#pendingRemovals.delete(run);
        removePicture();
      };
      this.#pendingRemovals.add(run);
      setTimeout(run, FALLBACK_MS);
    } else removePicture();
  }
}

/**
 * Konkord: a sender on a connection with encoded streams on (the shared encode)
 * only sends what is written to it: its frames just go through
 */
function passFramesOn(sender: RTCRtpSender) {
  const { readable, writable } = (
    sender as unknown as {
      createEncodedStreams(): {
        readable: ReadableStream<unknown>;
        writable: WritableStream<unknown>;
      };
    }
  ).createEncodedStreams();
  void readable.pipeTo(writable).catch(() => {});
}

/**
 * Konkord: asks for the sound in stereo, at full quality (games and music). The
 * browser's default is mono.
 */
function stereoSound(sdp: string) {
  const opus = [...sdp.matchAll(/^a=rtpmap:(\d+) opus\/48000\/2\r?$/gim)].map(
    (match) => match[1],
  );
  return opus.reduce(
    (text, payload) =>
      text.replace(
        new RegExp(`^(a=fmtp:${payload} )(?!.*stereo=1)(.*?)(\\r?)$`, "m"),
        `$1$2;stereo=1;sprop-stereo=1;maxaveragebitrate=${STREAM_SOUND_BITRATE}$3`,
      ),
    sdp,
  );
}

/**
 * Konkord: H.264 first, the codec browsers encode on the GPU / integrated
 * graphics (VP8 / VP9 / AV1 run on the processor). The browser's own order is
 * kept among the H.264 profiles: tested on Chromium / Windows, its first one
 * (Baseline 42001f) runs on the GPU while Constrained Baseline (42e01f) falls
 * back to OpenH264 on the processor.
 */
function preferH264(transceiver: RTCRtpTransceiver) {
  // Konkord: not in Firefox, its H.264 runs on the processor and stalls
  if (!prefersH264Encoding()) return;
  const codecs = RTCRtpSender.getCapabilities?.("video")?.codecs;
  if (!codecs || !transceiver.setCodecPreferences) return;
  const rank = (codec: RTCRtpCodec) =>
    codec.mimeType.toLowerCase() === "video/h264" ? 0 : 1;
  try {
    transceiver.setCodecPreferences(
      [...codecs].sort((a, b) => rank(a) - rank(b)),
    );
  } catch {
    // keep the browser's order
  }
}

/**
 * Konkord: only H.264, for connections carrying the shared encode (its
 * frames are H.264 whatever the stand-in would have been)
 */
/**
 * Konkord: a Main / High H.264 format the connection agreed on (the GPU
 * encodes those), when the computer drops Baseline from its own offers
 * because Baseline runs on the processor (see h264Hardware.ts)
 */
function gpuH264Codec(params: RTCRtpSendParameters) {
  if (h264Hardware() !== true) return;
  return params.codecs?.find(
    (codec) =>
      codec.mimeType.toLowerCase() === "video/h264" &&
      /profile-level-id=(4d|64)/i.test(codec.sdpFmtpLine ?? "") &&
      /packetization-mode=1/.test(codec.sdpFmtpLine ?? ""),
  );
}

function onlyH264(transceiver: RTCRtpTransceiver) {
  const codecs = RTCRtpSender.getCapabilities?.("video")?.codecs ?? [];
  // Konkord: Constrained Baseline first, the profile the shared encode
  // makes, so that is what gets agreed (every browser decodes it)
  const rank = (codec: RTCRtpCodec) =>
    /profile-level-id=42e0/i.test(codec.sdpFmtpLine ?? "") ? 0 : 1;
  const h264 = codecs
    .filter((codec) => codec.mimeType.toLowerCase() === "video/h264")
    .sort((a, b) => rank(a) - rank(b));
  if (!h264.length || !transceiver.setCodecPreferences) return;
  try {
    transceiver.setCodecPreferences(h264);
  } catch {
    // keep the browser's order
  }
}

/**
 * Konkord: a direct stream's receive stats, shaped like livekit's
 * getReceiverStats so the stats panel reads both the same way
 */
export async function directReceiverStats(receiver: RTCRtpReceiver) {
  const report = await receiver.getStats().catch(() => undefined);
  if (!report) return;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  let inbound: any;
  report.forEach((stat) => {
    if (stat.type === "inbound-rtp" && stat.kind === "video") inbound = stat;
  });
  if (!inbound) return;
  const codec = inbound.codecId ? report.get(inbound.codecId) : undefined;
  return {
    timestamp: inbound.timestamp as number,
    bytesReceived: inbound.bytesReceived as number | undefined,
    framesDecoded: inbound.framesDecoded as number | undefined,
    packetsLost: inbound.packetsLost as number | undefined,
    packetsReceived: inbound.packetsReceived as number | undefined,
    framesDropped: inbound.framesDropped as number | undefined,
    frameWidth: inbound.frameWidth as number | undefined,
    frameHeight: inbound.frameHeight as number | undefined,
    decoderImplementation: inbound.decoderImplementation as string | undefined,
    mimeType: codec?.mimeType as string | undefined,
  };
}

/**
 * Konkord: frames per second the screen capture delivers, before any encoding
 * (from any sender of our stream)
 */
export async function captureFps(sender?: RTCRtpSender) {
  const report = await sender?.getStats().catch(() => undefined);
  let fps: number | undefined;
  report?.forEach((stat) => {
    if (stat.type === "media-source" && stat.kind === "video")
      fps = stat.framesPerSecond;
  });
  return fps;
}

/**
 * Konkord: encoder of our stream's copy to the server (its best layer)
 */
export async function senderEncoder(sender?: RTCRtpSender) {
  const report = await sender?.getStats().catch(() => undefined);
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  let best: any;
  report?.forEach((stat) => {
    if (
      stat.type === "outbound-rtp" &&
      stat.kind === "video" &&
      (stat.frameWidth ?? 0) >= (best?.frameWidth ?? 0)
    )
      best = stat;
  });
  if (!best?.encoderImplementation) return;
  return {
    name: best.encoderImplementation as string,
    hardware: best.powerEfficientEncoder as boolean | undefined,
  };
}
